import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import GoalCard from './components/GoalCard';
import GoalFilters from './components/GoalFilters';
import AddGoalModal from './components/AddGoalModal';
import GoalDetailView from './components/GoalDetailView';
import CollectDataButton from '../../components/CollectDataButton';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useStudents } from 'context/StudentsContext';
import { humanizeSupabaseError } from '../../lib/supabaseErrors';

// localStorage key that remembers whether the teacher prefers the goals
// grouped by student (default) or all in one flat grid. Persisted so a
// teacher who really wants the flat view doesn't have to re-pick it every
// visit. Namespaced (`edutracker.` prefix + `.v1` suffix) so we can
// evolve the shape later without colliding.
const VIEW_MODE_KEY = 'edutracker.goalsViewMode.v1';

// ---------------------------------------------------------------------------
// IEP Goals & Objectives Tracking — real, Supabase-backed page.
//
// Replaces the previous mock-data version. Reads from and writes to
// public.behavior_goals via IEPGoalsContext. Three ways in for teachers:
//
//   1. Write the goal by hand.
//   2. Start from a curated SMART template (see lib/iepGoalTemplates.js).
//   3. Draft with AI using their own OpenAI API key (see lib/goalAi.js).
//
// All three roads deposit the same shape in the DB, so downstream
// analytics/reports see one consistent data model.
// ---------------------------------------------------------------------------

const IEPGoalsAndObjectivesTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedStudentId =
    location?.state?.studentId || location?.state?.selectedStudentId || '';
  // Optional deep-link from the Collect Data launcher: land on this page
  // with a goal already open in the detail view.
  const openGoalIdFromState = location?.state?.openGoalId || null;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    domain: 'all',
    status: 'all',
    sortBy: 'recent',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [viewingGoalId, setViewingGoalId] = useState(null);
  const [pageError, setPageError] = useState(null);

  // Grouped-by-student is the default because a caseload of 20 students
  // × ~4 goals each = 80 cards in the old flat view — a wall of text a
  // teacher has to scroll past to find the one goal she wants to log
  // data against. Grouped view keeps all 20 students visible at a glance
  // and hides their goals until she taps in. Preference is remembered
  // in localStorage per browser (not per user, intentionally — the
  // choice is about tiny screen space vs. one big list, not identity).
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grouped';
    try {
      const saved = window.localStorage.getItem(VIEW_MODE_KEY);
      return saved === 'flat' ? 'flat' : 'grouped';
    } catch {
      return 'grouped';
    }
  });

  const setPersistedViewMode = useCallback((mode) => {
    setViewMode(mode);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // localStorage disabled — accept in-memory only.
    }
  }, []);

  // Set of studentIds whose accordion panel is open. Empty by default
  // (all collapsed on first visit) so the initial view is quiet.
  // Reopening the page later starts fresh — intentionally not persisted
  // because expansion state feels like a per-visit thing.
  const [expandedStudentIds, setExpandedStudentIds] = useState(() => new Set());

  const toggleStudentExpanded = useCallback((studentId) => {
    setExpandedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }, []);

  const {
    goals,
    loading,
    error: goalsLoadError,
    isLive,
    removeGoal,
  } = useIEPGoals();
  const { students } = useStudents();

  // Map studentId -> "First Last" (or "Unnamed student") so we can render
  // student names on each card without doing a lookup every render.
  const studentNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(students) ? students : []).forEach((s) => {
      const name =
        `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student';
      map.set(s.id, name);
    });
    return map;
  }, [students]);

  const filteredGoals = useMemo(() => {
    const list = Array.isArray(goals) ? goals : [];
    const search = searchQuery.trim().toLowerCase();

    return list
      .filter((goal) => {
        if (filters.domain !== 'all' && goal.goalArea !== filters.domain) {
          return false;
        }
        if (filters.status !== 'all' && goal.status !== filters.status) {
          return false;
        }
        if (search) {
          const studentName = studentNameById.get(goal.studentId) || '';
          const hay = [
            goal.specific,
            goal.target,
            goal.measurable,
            goal.baseline,
            goal.masteryCriteria,
            studentName,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!hay.includes(search)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        switch (filters.sortBy) {
          case 'progress':
            return (b.progressPercent || 0) - (a.progressPercent || 0);
          case 'dueDate': {
            const aTime = a.targetDate ? new Date(a.targetDate).getTime() : Infinity;
            const bTime = b.targetDate ? new Date(b.targetDate).getTime() : Infinity;
            return aTime - bTime;
          }
          case 'alphabetical':
            return (a.specific || '').localeCompare(b.specific || '');
          case 'recent':
          default: {
            const aTime = a.lastUpdated
              ? new Date(a.lastUpdated).getTime()
              : a.createdAt
                ? new Date(a.createdAt).getTime()
                : 0;
            const bTime = b.lastUpdated
              ? new Date(b.lastUpdated).getTime()
              : b.createdAt
                ? new Date(b.createdAt).getTime()
                : 0;
            return bTime - aTime;
          }
        }
      });
  }, [goals, filters, searchQuery, studentNameById]);

  const summary = useMemo(() => {
    const list = Array.isArray(goals) ? goals : [];
    return {
      total: list.length,
      inProgress: list.filter((g) => g.status === 'in_progress').length,
      mastered: list.filter((g) => g.status === 'mastered').length,
      notStarted: list.filter((g) => g.status === 'not_started').length,
    };
  }, [goals]);

  // Bucket filteredGoals under their students, preserving the order the
  // student roster is in (so a teacher's mental map of "Sam is near the
  // top" doesn't shift each time she opens the page).
  //
  // Students with zero matching goals after the current filters + search
  // are omitted — that's the whole point of grouping under a filter.
  // An "Unknown student" bucket catches any goal whose studentId
  // doesn't match a known roster entry (rare, but happens if a student
  // is deleted while their goals aren't cascaded — better to show than
  // to hide).
  const studentGroups = useMemo(() => {
    const bucketMap = new Map();
    for (const goal of filteredGoals) {
      const key = goal.studentId || '__unknown__';
      let bucket = bucketMap.get(key);
      if (!bucket) {
        bucket = { studentId: key, goals: [] };
        bucketMap.set(key, bucket);
      }
      bucket.goals.push(goal);
    }

    const rosterOrder = (Array.isArray(students) ? students : []).map(
      (s) => s.id,
    );
    const seen = new Set();
    const ordered = [];

    for (const id of rosterOrder) {
      if (bucketMap.has(id)) {
        const bucket = bucketMap.get(id);
        const student = students.find((s) => s.id === id);
        ordered.push({
          ...bucket,
          studentName:
            (student &&
              `${student.firstName || ''} ${student.lastName || ''}`.trim()) ||
            'Unnamed student',
          gradeLabel: student?.gradeLevel || student?.grade || null,
        });
        seen.add(id);
      }
    }
    for (const [key, bucket] of bucketMap.entries()) {
      if (seen.has(key)) continue;
      ordered.push({
        ...bucket,
        studentName:
          key === '__unknown__'
            ? 'Unknown student'
            : studentNameById.get(key) || 'Unnamed student',
        gradeLabel: null,
      });
    }
    return ordered;
  }, [filteredGoals, students, studentNameById]);

  // Auto-expand behavior when the teacher searches or when there's only
  // one student showing:
  //  - Searching and getting matches on 3 students → open all 3 so she
  //    doesn't have to tap 3 more times.
  //  - Only 1 student in the current view (post-filter) → open it.
  //  - Otherwise leave whatever's currently expanded alone so accidental
  //    filter tweaks don't yank a panel closed while she's reading.
  useEffect(() => {
    if (viewMode !== 'grouped') return;
    const trimmed = searchQuery.trim();
    if (!trimmed && studentGroups.length !== 1) return;
    setExpandedStudentIds((prev) => {
      const next = new Set(prev);
      for (const group of studentGroups) {
        next.add(group.studentId);
      }
      return next;
    });
  }, [searchQuery, studentGroups, viewMode]);

  const areAllExpanded =
    studentGroups.length > 0 &&
    studentGroups.every((g) => expandedStudentIds.has(g.studentId));

  const handleExpandAll = () => {
    setExpandedStudentIds(new Set(studentGroups.map((g) => g.studentId)));
  };

  const handleCollapseAll = () => {
    setExpandedStudentIds(new Set());
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ domain: 'all', status: 'all', sortBy: 'recent' });
    setSearchQuery('');
  };

  const handleCreateNewGoal = () => {
    setEditingGoal(null);
    setIsModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setIsModalOpen(true);
  };

  const handleOpenGoal = (goal) => {
    if (!goal?.id) return;
    setViewingGoalId(goal.id);
  };

  const handleCloseDetail = () => {
    setViewingGoalId(null);
  };

  const handleDeleteGoal = async (goal) => {
    setPageError(null);
    const label = goal?.specific
      ? `"${goal.specific.slice(0, 80)}${goal.specific.length > 80 ? '…' : ''}"`
      : 'this goal';
    const confirmed = window.confirm(
      `Delete ${label}?\n\nThis cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await removeGoal(goal.id);
    } catch (err) {
      setPageError(
        humanizeSupabaseError(err, 'Could not delete this goal. Please try again.'),
      );
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingGoal(null);
  };

  // If we arrived here from the "Collect Data" launcher, auto-open the
  // detail view as soon as goals finish loading. Then wipe the state off
  // the location so a browser refresh doesn't keep re-opening the modal.
  useEffect(() => {
    if (!openGoalIdFromState) return;
    if (loading) return;
    const target = (Array.isArray(goals) ? goals : []).find(
      (g) => g.id === openGoalIdFromState,
    );
    if (target) {
      setViewingGoalId(target.id);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [openGoalIdFromState, goals, loading, navigate, location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6">
          <Breadcrumb />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                IEP Goals & Objectives Tracking
              </h1>
              <p className="text-muted-foreground">
                Write your own SMART goals, start from a template, or draft
                with AI. Every goal is saved to your student's record.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
                  isLive
                    ? 'bg-success/10 text-success border-success/30'
                    : 'bg-warning/10 text-warning border-warning/30'
                }`}
                title={
                  isLive
                    ? 'Connected to Supabase'
                    : 'Local demo mode — data stays in this browser'
                }
              >
                <Icon name={isLive ? 'Cloud' : 'HardDrive'} size={14} />
                {isLive ? 'Live sync' : 'Demo mode'}
              </div>
              {/* One-tap "log data on a goal" button. Skips the type picker
                  because we're already on the IEP Goals page — the launcher
                  just asks student → goal, then opens the detail view. */}
              <CollectDataButton
                modes={['iep']}
                label="Log Data"
                variant="outline"
                iconName="ClipboardEdit"
              />
              <Button
                variant="outline"
                onClick={() => navigate('/goals/import')}
                title="Bulk import IEP goals from a spreadsheet (EdPlan, SEIS, Frontline, etc.)"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Import from CSV
              </Button>
              <Button onClick={handleCreateNewGoal}>
                <Icon name="Plus" size={16} className="mr-2" />
                New Goal
              </Button>
            </div>
          </div>

          {/* Errors */}
          {(pageError || goalsLoadError) && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
              {pageError || goalsLoadError}
            </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Total goals</div>
              <div className="text-2xl font-bold text-foreground">
                {summary.total}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">In progress</div>
              <div className="text-2xl font-bold text-primary">
                {summary.inProgress}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Mastered</div>
              <div className="text-2xl font-bold text-success">
                {summary.mastered}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Not started</div>
              <div className="text-2xl font-bold text-muted-foreground">
                {summary.notStarted}
              </div>
            </div>
          </div>

          {/* Filters */}
          <GoalFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            totalGoals={goals?.length || 0}
            filteredCount={filteredGoals.length}
          />

          {/* View-mode + expand/collapse controls. Small, secondary row —
              this is a preference, not a primary action, so it's styled
              like a pill toggle rather than a big button. */}
          {filteredGoals.length > 0 && (
            <div className="mt-3 mb-4 flex flex-wrap items-center justify-between gap-3">
              <div
                role="group"
                aria-label="Goals layout"
                className="inline-flex rounded-md border border-border bg-card p-0.5"
              >
                <button
                  type="button"
                  onClick={() => setPersistedViewMode('grouped')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    viewMode === 'grouped'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-pressed={viewMode === 'grouped'}
                >
                  <Icon name="Users" size={14} className="inline mr-1.5 -mt-0.5" />
                  Grouped by student
                </button>
                <button
                  type="button"
                  onClick={() => setPersistedViewMode('flat')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    viewMode === 'flat'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-pressed={viewMode === 'flat'}
                >
                  <Icon name="List" size={14} className="inline mr-1.5 -mt-0.5" />
                  All goals in one list
                </button>
              </div>

              {viewMode === 'grouped' && studentGroups.length > 1 && (
                <div className="flex items-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={
                      areAllExpanded ? handleCollapseAll : handleExpandAll
                    }
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80 hover:underline"
                  >
                    <Icon
                      name={areAllExpanded ? 'ChevronsDownUp' : 'ChevronsUpDown'}
                      size={14}
                    />
                    {areAllExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Goals list */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon
                name="Loader2"
                size={32}
                className="animate-spin mx-auto mb-2"
              />
              Loading goals…
            </div>
          ) : filteredGoals.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon
                name="Target"
                size={64}
                className="mx-auto mb-4 text-muted-foreground opacity-50"
              />
              <h3 className="text-lg font-medium mb-2">
                {goals?.length === 0
                  ? 'No goals yet'
                  : 'No goals match your filters'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {goals?.length === 0
                  ? 'Create your first IEP goal to get started. You can write it yourself, use a template, or draft with AI.'
                  : 'Try adjusting your search or clearing filters.'}
              </p>
              {goals?.length === 0 ? (
                <Button onClick={handleCreateNewGoal}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create first goal
                </Button>
              ) : (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : viewMode === 'grouped' ? (
            <div className="space-y-3">
              {studentGroups.map((group) => (
                <StudentGoalGroup
                  key={group.studentId}
                  group={group}
                  isExpanded={expandedStudentIds.has(group.studentId)}
                  onToggle={() => toggleStudentExpanded(group.studentId)}
                  onOpenGoal={handleOpenGoal}
                  onEditGoal={handleEditGoal}
                  onDeleteGoal={handleDeleteGoal}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  studentName={studentNameById.get(goal.studentId)}
                  onSelect={handleOpenGoal}
                  onEdit={handleEditGoal}
                  onDelete={handleDeleteGoal}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <AddGoalModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        defaultStudentId={preselectedStudentId}
        existingGoal={editingGoal}
      />

      {viewingGoalId && (() => {
        const viewingGoal = (Array.isArray(goals) ? goals : []).find(
          (g) => g.id === viewingGoalId,
        );
        if (!viewingGoal) return null;
        return (
          <GoalDetailView
            goal={viewingGoal}
            studentName={studentNameById.get(viewingGoal.studentId)}
            onClose={handleCloseDetail}
          />
        );
      })()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StudentGoalGroup — one collapsible accordion panel per student.
//
// Header (always visible, clickable full width):
//   • Chevron (▶ collapsed / ▼ open)
//   • Student name
//   • Grade pill (if we have it)
//   • Goal count ("3 goals") + tiny status breakdown ("2 in progress · 1
//     mastered") so a teacher can spot progress at a glance without
//     having to expand every panel.
//
// Body (only when expanded): the student's goal cards, stacked. Cards
// omit the student-name meta row because we're already inside their
// group. Cards render at full width on phones and 2-up on desktop —
// tighter than the flat view's 3-up grid because inside a group we're
// prioritizing readability over density.
//
// The whole header is a real <button> for keyboard + screen-reader
// support (aria-expanded reflects state, aria-controls points at the
// body panel id).
// ---------------------------------------------------------------------------
const StudentGoalGroup = ({
  group,
  isExpanded,
  onToggle,
  onOpenGoal,
  onEditGoal,
  onDeleteGoal,
}) => {
  const panelId = `student-goals-${group.studentId}`;

  const statusCounts = useMemo(() => {
    const counts = {
      in_progress: 0,
      mastered: 0,
      not_started: 0,
      discontinued: 0,
    };
    for (const g of group.goals) {
      if (counts[g.status] !== undefined) counts[g.status] += 1;
    }
    return counts;
  }, [group.goals]);

  const goalCount = group.goals.length;
  const goalCountLabel = `${goalCount} ${goalCount === 1 ? 'goal' : 'goals'}`;

  const statusChips = [
    { key: 'in_progress', label: 'in progress', className: 'text-primary' },
    { key: 'mastered', label: 'mastered', className: 'text-success' },
    { key: 'not_started', label: 'not started', className: 'text-muted-foreground' },
  ]
    .map((chip) => ({ ...chip, count: statusCounts[chip.key] }))
    .filter((chip) => chip.count > 0);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
      >
        <Icon
          name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
          size={20}
          className="flex-shrink-0 text-muted-foreground"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h3 className="font-semibold text-foreground text-base truncate">
              {group.studentName}
            </h3>
            {group.gradeLabel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {group.gradeLabel}
              </span>
            )}
            <span className="text-sm font-medium text-muted-foreground">
              {goalCountLabel}
            </span>
          </div>
          {statusChips.length > 0 && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {statusChips.map((chip, idx) => (
                <span key={chip.key} className="inline-flex items-center gap-1">
                  {idx > 0 && <span className="text-muted-foreground/50">·</span>}
                  <span className={`font-medium ${chip.className}`}>
                    {chip.count}
                  </span>
                  <span>{chip.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </button>

      {isExpanded && (
        <div
          id={panelId}
          className="border-t border-border bg-background/40 px-4 py-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {group.goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                studentName={null}
                onSelect={onOpenGoal}
                onEdit={onEditGoal}
                onDelete={onDeleteGoal}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IEPGoalsAndObjectivesTracking;
